'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

interface ScreenData {
  width: number;
  height: number;
  dpr: number;
}

interface TrackingData {
  timestamp: string;
  sessionHash: string;
  pathname: string;
  url: string;
  referrer: string;
  userAgent: string;
  screen: ScreenData;
  language: string;
  platform: string;
  timezone: string;
  isBot: boolean;
}

// Define a custom Navigator type that includes non-standard properties
interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number; // Optional as it might not be available
}

/**
 * Generate a session hash based on user agent and other factors
 * This should match the hash generation in middleware.ts
 */
async function generateClientSessionHash(): Promise<string> {
  const userAgent = navigator.userAgent;
  // Cast navigator to our extended type
  const extendedNavigator = navigator as NavigatorWithMemory;

  // Note: We don't have IP in the client, so we'll use what we have available
  const components = [
    userAgent,
    navigator.language,
    navigator.hardwareConcurrency?.toString() || '',
    // Use the extended type
    (extendedNavigator.deviceMemory || '').toString(),
    navigator.platform || ''
  ].join('-');

  // Use the Web Crypto API for hashing
  const encoder = new TextEncoder();
  const data = encoder.encode(components);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  
  return hashHex;
}

/**
 * Check if the user is likely a bot
 */
function isLikelyBot(): boolean {
  const userAgent = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'bot', 'crawler', 'spider', 'lighthouse', 'slurp',
    'facebook', 'twitter', 'wget', 'curl', 'python'
  ];
  return botPatterns.some(pattern => userAgent.includes(pattern)) || navigator.webdriver;
}

function ClientTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only track on client side
    if (typeof window === 'undefined') return;

    // Function to send tracking data with client-side values
    const sendTrackingData = async () => {
      try {
        // Get session hash that should match the middleware
        const sessionHash = await generateClientSessionHash();
        
        // Get screen data
        const screenData: ScreenData = {
          width: window.innerWidth || window.screen.width,
          height: window.innerHeight || window.screen.height,
          dpr: window.devicePixelRatio || 1
        };
        
        // Create URL including search parameters
        const url = `${window.location.origin}${pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
        
        // Create tracking data
        const trackingData: TrackingData = {
          timestamp: new Date().toISOString(),
          sessionHash,
          pathname,
          url,
          referrer: document.referrer || '',
          userAgent: navigator.userAgent,
          screen: screenData,
          language: navigator.language,
          platform: navigator.platform || '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
          isBot: isLikelyBot()
        };
        
        // Send data to tracking API via our own API route handler
        const trackingApiUrl = '/api/track'; // Use the relative path to our handler
        await fetch(trackingApiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(trackingData),
          // Use a shorter timeout to prevent long page load times
          // and make it non-blocking. Increased timeout from 1s to 3s.
          signal: AbortSignal.timeout(3000)
        });
      } catch (error) {
        // Silent fail - don't break the application if tracking fails
        console.error('[ClientTracker] Error sending client tracking data:', error);
      }
    };

    // Send tracking data after a small delay to ensure page is fully loaded
    const timeoutId = setTimeout(() => {
      sendTrackingData();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [pathname, searchParams]); // Re-send when pathname or search params change

  return null; // This component doesn't render anything
}

export default ClientTracker; 